const TARGET_SECTION_SELECTORS = [
  '.darkrim-review-cards-wrap',
  '.darkrim-player-gallery-wrap',
  '.darkrim-app-promo-wrap',
  '.darkrim-featured-blog-wrap',
  '.darkrim-community-wrap',
];

class DarkrimHomeBackground extends HTMLElement {
  #layer = null;
  #main = null;
  #resizeObserver = null;
  #sections = [];
  #structureFrame = null;
  #updateFrame = null;

  connectedCallback() {
    const template = this.querySelector('template');
    this.#main = document.querySelector('#MainContent');

    if (!template?.content.querySelector('.darkrim-rsp-media') || !this.#main) return;

    this.#layer = document.createElement('div');
    this.#layer.className = 'darkrim-home-background';
    this.#layer.dataset.ownerSection = this.dataset.sectionId || '';
    this.#layer.setAttribute('aria-hidden', 'true');
    this.#layer.append(template.content.cloneNode(true));

    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#bindSections();

    window.addEventListener('resize', this.#scheduleUpdate, { passive: true });
    document.addEventListener('shopify:section:load', this.#handleSectionChange);
    document.addEventListener('shopify:section:unload', this.#handleSectionChange);
    document.addEventListener('shopify:section:reorder', this.#handleSectionChange);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#scheduleUpdate);
    document.removeEventListener('shopify:section:load', this.#handleSectionChange);
    document.removeEventListener('shopify:section:unload', this.#handleSectionChange);
    document.removeEventListener('shopify:section:reorder', this.#handleSectionChange);

    if (this.#updateFrame !== null) window.cancelAnimationFrame(this.#updateFrame);
    if (this.#structureFrame !== null) window.cancelAnimationFrame(this.#structureFrame);

    this.#sections.forEach((section) => section.classList.remove('darkrim-home-background__section'));
    this.#layer?.remove();

    if (!this.#main?.querySelector('.darkrim-home-background')) {
      this.#main?.classList.remove('darkrim-home-background-active');
    }
  }

  #handleSectionChange = () => {
    if (this.#structureFrame !== null) window.cancelAnimationFrame(this.#structureFrame);

    this.#structureFrame = window.requestAnimationFrame(() => {
      this.#structureFrame = null;
      if (this.isConnected) this.#bindSections();
    });
  };

  #bindSections = () => {
    if (!this.#main || !this.#layer) return;

    this.#resizeObserver?.disconnect();
    this.#sections.forEach((section) => section.classList.remove('darkrim-home-background__section'));

    this.#sections = TARGET_SECTION_SELECTORS.map((selector) =>
      this.#main.querySelector(selector)?.closest('.shopify-section'),
    ).filter(Boolean);

    if (this.#sections.length !== TARGET_SECTION_SELECTORS.length) {
      this.#layer.remove();
      this.#main.classList.remove('darkrim-home-background-active');
      return;
    }

    const [startSection] = this.#sections;
    const endSection = this.#sections[this.#sections.length - 1];
    const orderedSections = Array.from(this.#main.children);

    if (orderedSections.indexOf(startSection) >= orderedSections.indexOf(endSection)) {
      this.#layer.remove();
      this.#main.classList.remove('darkrim-home-background-active');
      return;
    }

    this.#main.insertBefore(this.#layer, startSection);
    this.#main.classList.add('darkrim-home-background-active');
    this.#resizeObserver?.observe(this.#main);

    this.#sections.forEach((section) => {
      section.classList.add('darkrim-home-background__section');
      this.#resizeObserver?.observe(section);
    });

    this.#scheduleUpdate();
  };

  #scheduleUpdate = () => {
    if (this.#updateFrame !== null) window.cancelAnimationFrame(this.#updateFrame);

    this.#updateFrame = window.requestAnimationFrame(() => {
      this.#updateFrame = null;
      this.#updateLayerBounds();
    });
  };

  #updateLayerBounds() {
    if (!this.#main || !this.#layer || this.#sections.length !== TARGET_SECTION_SELECTORS.length) return;

    const [startSection] = this.#sections;
    const mainRect = this.#main.getBoundingClientRect();
    const startRect = startSection.getBoundingClientRect();

    this.#layer.style.top = `${startRect.top - mainRect.top + this.#main.scrollTop}px`;
  }
}

if (!customElements.get('darkrim-home-background')) {
  customElements.define('darkrim-home-background', DarkrimHomeBackground);
}
